import { ArrowBack, ChevronLeft } from '@mui/icons-material';
import { Button, Stack, SxProps, Theme, Typography, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid2 from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import React, { PropsWithChildren } from 'react';

import bg from '@/assets/img/bg/bg2.png';
import { ViewWrapperPaperStyles } from '@/components/viewWrapper/viewWrapper.styles.ts';
import { APP_CONTENT_MIN_HEIGHT } from '@/config/layout.constants.ts';
import { usePageContext } from '@/contexts/pageContext/Page.context.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

type Props = PropsWithChildren & {
  additionalHeaderContent?: React.ReactNode;
  backLabel?: string;
  hidePaper?: boolean;
  isLoading?: boolean;
  maxWidth?: false | string;
  navigateFunction?: () => void;
  noBg?: boolean;
  sx?: SxProps<Theme>;
  title?: string;
  useGutter?: boolean;
};

type WrapperProps = Omit<Props, 'isLoading'>;

export const ViewWrapper: React.FC<Props> = props => {
  const { colors } = useTheme();
  const { pageDetails } = usePageContext();
  const { t } = useTranslationWithPrefix('pageTitles');

  const { children, isLoading, title } = props;

  if (isLoading) {
    return (
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          p: 5,
          width: '100%',
        }}
      >
        <CircularProgress sx={{ color: colors.accentInk }} />
      </Stack>
    );
  }

  return (
    <Wrapper {...props} title={title ?? t(pageDetails.title)}>
      <Box>{children}</Box>
    </Wrapper>
  );
};

const Wrapper: React.FC<WrapperProps> = props => {
  const { colors } = useTheme();
  const {
    additionalHeaderContent,
    backLabel,
    children,
    hidePaper,
    maxWidth,
    navigateFunction,
    noBg,
    sx,
    title,
    useGutter,
  } = props;
  const mainStyles = { ...ViewWrapperPaperStyles, ...(sx ?? {}) };

  if (noBg) {
    const noBgStyles: SxProps<Theme> = {
      maxWidth: maxWidth === false ? undefined : (maxWidth ?? 'none'),
      minHeight: APP_CONTENT_MIN_HEIGHT,
      mx: 'auto',
      px: { md: 4, xs: 2 },
      py: { md: 4, xs: 3 },
    };
    const noBgSx = { ...noBgStyles, ...(sx ?? {}) };

    return (
      <Box sx={noBgSx}>
        {navigateFunction && (
          <Button
            onClick={navigateFunction}
            startIcon={<ArrowBack />}
            sx={theme => ({
              '&:active': {
                scale: '0.96',
              },
              '&:hover': {
                color: theme.colors.textPrimary,
              },
              alignItems: 'center',
              alignSelf: 'flex-start',
              color: theme.colors.textSecondary,
              fontSize: '13px',
              fontWeight: 500,
              mb: 1,
              minHeight: 32,
              p: 0,
              transition: 'color 180ms cubic-bezier(0.2,0,0,1), scale 120ms',
            })}
            variant="text"
          >
            {backLabel ?? 'Wróć na Start'}
          </Button>
        )}
        {title && (
          <Typography
            component="h1"
            sx={theme => ({
              color: theme.colors.textPrimary,
              fontSize: { md: 28, xs: 24 },
              fontWeight: 700,
              lineHeight: 1.15,
              mb: 3,
              textWrap: 'balance',
            })}
          >
            {title}
          </Typography>
        )}
        {children}
      </Box>
    );
  }

  if (hidePaper) {
    return (
      <Container fixed maxWidth="xl" sx={mainStyles}>
        {children}
      </Container>
    );
  }

  return (
    <Container
      sx={{
        ...mainStyles,
        background: `url(${bg})`,
        backgroundSize: 'fill',
        display: 'flex',
        flexDirection: 'column',
        minHeight: APP_CONTENT_MIN_HEIGHT,
      }}
    >
      <Stack spacing={1} sx={{ flexGrow: 1, mt: 12 }}>
        {title && (
          <Grid2 container sx={{ justifyContent: 'center' }}>
            <Grid2 size={{ lg: 9, md: 10, sm: 11, xs: 12 }}>
              <Stack
                direction={{ md: 'row', xs: 'column' }}
                spacing={{ md: 0, xs: 1 }}
                sx={{
                  alignItems: 'flex-end',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Stack
                  direction="row"
                  sx={{
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  {navigateFunction && (
                    <IconButton onClick={navigateFunction}>
                      <ChevronLeft
                        sx={{
                          color: colors.textPrimary,
                          cursor: 'pointer',
                          fontSize: { sm: 56, xs: 40 },
                        }}
                      />
                    </IconButton>
                  )}
                  <Typography
                    color="white"
                    component="h1"
                    sx={{
                      fontSize: { md: '40px', sm: '32px', xs: '24px' },
                    }}
                    variant="newHeader"
                  >
                    {title}
                  </Typography>
                </Stack>
                <Box sx={{ width: { md: 'auto', xs: '100%' } }}>{additionalHeaderContent}</Box>
              </Stack>
            </Grid2>
          </Grid2>
        )}
        <Paper
          square
          sx={{
            ...ViewWrapperPaperStyles,
            flexGrow: 1,
            overflow: 'visible', // Allow sticky positioning
          }}
        >
          <Grid2
            container
            sx={{
              justifyContent: 'center',
              overflow: 'visible', // Allow sticky positioning
              py: 2,
            }}
          >
            <Grid2
              size={{
                lg: useGutter ? 9 : 12,
                md: useGutter ? 10 : 12,
                sm: useGutter ? 11 : 12,
                xs: 12,
              }}
              sx={{ overflow: 'visible' }}
            >
              {children}
            </Grid2>
          </Grid2>
        </Paper>
      </Stack>
    </Container>
  );
};
